import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"

interface EmailRecord {
  id: string
  subject: string
  to: string
  from: string
  date: string
  openCount?: number
  content?: string
  userId?: string
}

interface Props {
  show: boolean
  toggle: (section: string) => void
}

const CompanyEmailLogSettings: React.FC<Props> = ({ show, toggle }) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()

  const [emails, setEmails] = useState<EmailRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [previewEmail, setPreviewEmail] = useState<EmailRecord | null>(null)

  // Test Digest Modal
  const [showTestModal, setShowTestModal] = useState(false)
  const [testType, setTestType] = useState<"activity" | "missing_code">("activity")
  const [targetEmail, setTargetEmail] = useState(user?.Email || "melvin.gonzalez@shearly.com")
  const [isSendingTest, setIsSendingTest] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const loadEmails = async (pageIndex = 0, searchFilter = search) => {
    if (!user?.OrganizationId) return
    try {
      setIsLoading(true)
      const qs = `OrganizationId=${user.OrganizationId}&Page=${pageIndex}&Quantity=25${
        searchFilter ? `&Search=${encodeURIComponent(searchFilter)}` : ""
      }`
      const res = await API.getAction("Organization/EmailHistory", qs)
      if (res.data?.status === "ok") {
        setEmails(res.data.response || [])
        setTotalPages(res.data.quantityOfPages || 1)
        setTotalRecords(res.data.quantityOfRecords || 0)
        setPage(pageIndex)
      }
    } catch (ex) {
      console.error("Error cargando historial de correos:", ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      loadEmails(0, "")
    }
  }, [show])

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        loadEmails(0, search)
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [search])

  const handleSendTest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.OrganizationId || !targetEmail.trim()) return
    try {
      setIsSendingTest(true)
      setTestResult(null)
      setTestError(null)

      const qs = `OrganizationId=${user.OrganizationId}&overrideEmail=${encodeURIComponent(targetEmail.trim())}`
      const actionName =
        testType === "activity"
          ? "Organization/SendWeeklyActivityDigestNow"
          : "Organization/SendMondayMissingClientCodeRemindersNow"

      const res = await API.getAction(actionName, qs)
      if (res.data?.status === "ok") {
        const msg =
          testType === "activity"
            ? t("Resumen semanal de actividad enviado exitosamente a ") + targetEmail.trim()
            : t("Recordatorio de lugares en venta sin código enviado exitosamente a ") + targetEmail.trim()
        setTestResult(msg)
        loadEmails(0, search)
      } else {
        setTestError(res.data?.message || t("Error al enviar el correo de prueba."))
      }
    } catch (ex: any) {
      setTestError(ex.message || t("Error de conexión al enviar el correo de prueba."))
    } finally {
      setIsSendingTest(false)
    }
  }

  if (!show) return null

  return (
    <>
      <Modal show={show} onHide={() => toggle("")} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <div className="d-flex align-items-center justify-content-between w-100 me-3">
            <div>
              <Modal.Title className="h5 fw-bold mb-1">
                <i className="fas fa-envelope-open-text text-primary me-2" />
                {t("Historial de Correos y Notificaciones")}
              </Modal.Title>
              <small className="text-muted">
                {t("Registro de correos y alertas enviados")} ({totalRecords} {t("registros")})
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                placeholder={t("Buscar por asunto o destinatario...")}
                style={{ maxWidth: 200 }}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Button
                variant="success"
                size="sm"
                className="fw-semibold text-nowrap"
                onClick={() => {
                  setTestResult(null)
                  setTestError(null)
                  setShowTestModal(true)
                }}
              >
                <i className="fas fa-paper-plane me-1" />
                {t("Disparar Pruebas")}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => loadEmails(page, search)}
                title={t("Recargar")}
              >
                <i className="fas fa-rotate" />
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2 text-primary" />
              <span className="small text-muted">{t("Cargando historial de correos...")}</span>
            </div>
          ) : emails.length === 0 ? (
            <Alert variant="light" className="text-center py-5 border-0 m-0 text-muted small">
              <i className="fas fa-inbox fa-2x mb-2 text-muted d-block" />
              <div>{t("No se registraron envíos de correo para esta organización.")}</div>
            </Alert>
          ) : (
            <>
              <Table hover responsive className="align-middle small mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{t("FECHA DE ENVÍO")}</th>
                    <th>{t("DESTINATARIO")}</th>
                    <th>{t("ASUNTO")}</th>
                    <th>{t("REMITENTE")}</th>
                    <th className="text-center">{t("APERTURAS")}</th>
                    <th className="text-end pe-3">{t("ACCIONES")}</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map(m => (
                    <tr key={m.id}>
                      <td className="ps-3 fw-semibold text-dark">
                        <i className="far fa-clock me-1 text-primary" />
                        {m.date ? moment(m.date).format("DD/MM/YYYY HH:mm") : "—"}
                      </td>
                      <td>
                        <Badge bg="light" text="dark" className="border font-monospace">
                          {m.to}
                        </Badge>
                      </td>
                      <td className="fw-bold text-dark">{m.subject || t("(Sin asunto)")}</td>
                      <td className="text-muted">{m.from || "Sistema Shearly"}</td>
                      <td className="text-center">
                        <Badge bg={m.openCount && m.openCount > 0 ? "success" : "secondary"} pill>
                          {m.openCount || 0} {t("lecturas")}
                        </Badge>
                      </td>
                      <td className="text-end pe-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-0 px-2"
                          title={t("Ver Detalles")}
                          onClick={() => setPreviewEmail(m)}
                        >
                          <i className="fas fa-eye me-1" />
                          {t("Detalles")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                  <small className="text-muted">
                    {t("Página")} {page + 1} {t("de")} {totalPages}
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page === 0 || isLoading}
                      onClick={() => loadEmails(page - 1, search)}
                    >
                      <i className="fas fa-chevron-left me-1" />
                      {t("Anterior")}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page >= totalPages - 1 || isLoading}
                      onClick={() => loadEmails(page + 1, search)}
                    >
                      {t("Siguiente")}
                      <i className="fas fa-chevron-right ms-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => toggle("")}>
            {t("Cerrar")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Test Triggers */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            <i className="fas fa-paper-plane text-success me-2" />
            {t("Disparar Envío de Prueba de Correos Programados")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendTest}>
          <Modal.Body className="small">
            <p className="text-muted mb-3">
              {t("Selecciona el tipo de correo programado que deseas generar y enviar en tiempo real a la dirección especificada para validar su contenido y formato.")}
            </p>

            {testResult && <Alert variant="success" dismissible onClose={() => setTestResult(null)}>{testResult}</Alert>}
            {testError && <Alert variant="danger" dismissible onClose={() => setTestError(null)}>{testError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">{t("Tipo de Correo Programado")} *</Form.Label>
              <Form.Select
                size="sm"
                value={testType}
                onChange={e => setTestType(e.target.value as any)}
              >
                <option value="activity">
                  📊 {t("Viernes 5:00 PM: Resumen Semanal de Actividad (Visitas y Nuevos Lugares a Administradores)")}
                </option>
                <option value="missing_code">
                  ⚠️ {t("Lunes 7:30 AM: Recordatorio a Agentes de Lugares en Venta sin Código de Cliente")}
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">{t("Correo de Destino")} *</Form.Label>
              <Form.Control
                type="email"
                size="sm"
                placeholder="ejemplo@empresa.com"
                value={targetEmail}
                onChange={e => setTargetEmail(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowTestModal(false)}>
              {t("Cerrar")}
            </Button>
            <Button variant="success" size="sm" type="submit" disabled={isSendingTest}>
              {isSendingTest ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("Generando y enviando...")}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-1" />
                  {t("Enviar Correo de Prueba")}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Preview Email */}
      <Modal show={!!previewEmail} onHide={() => setPreviewEmail(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            <i className="fas fa-envelope-open-text text-primary me-2" />
            {t("Detalles del Correo Enviado")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {previewEmail && (
            <div>
              <div className="p-3 bg-light rounded-3 border mb-3">
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="text-muted mb-1">{t("Para / Destinatario")}:</div>
                    <Badge bg="light" text="dark" className="border font-monospace fs-6">
                      {previewEmail.to}
                    </Badge>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted mb-1">{t("De / Remitente")}:</div>
                    <div className="fw-semibold text-dark">{previewEmail.from || "Sistema Shearly"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted mb-1">{t("Fecha de Envío")}:</div>
                    <div className="fw-semibold text-dark">
                      <i className="far fa-clock me-1 text-primary" />
                      {previewEmail.date ? moment(previewEmail.date).format("DD/MM/YYYY HH:mm:ss") : "—"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted mb-1">{t("Aperturas Registradas")}:</div>
                    <Badge bg={previewEmail.openCount && previewEmail.openCount > 0 ? "success" : "secondary"} pill>
                      {previewEmail.openCount || 0} {t("aperturas")}
                    </Badge>
                  </div>
                  <div className="col-12 border-top pt-2 mt-2">
                    <div className="text-muted mb-1">{t("Asunto")}:</div>
                    <div className="fw-bold fs-6 text-dark">{previewEmail.subject || t("(Sin asunto)")}</div>
                  </div>
                </div>
              </div>

              {previewEmail.content && (
                <div>
                  <div className="fw-semibold text-dark mb-2">{t("Contenido del Mensaje")}:</div>
                  <div
                    className="p-3 border rounded-3 bg-white"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                    dangerouslySetInnerHTML={{ __html: previewEmail.content }}
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setPreviewEmail(null)}>
            {t("Cerrar")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CompanyEmailLogSettings
